<template>
  <div>
    <PortalTarget
      name="portalTarget"
      multiple
    />
    <transition
      name="login-transition"
      mode="out-in"
    >
      <router-view />
    </transition>
  </div>
</template>

<script>
import kebabCase from 'lodash/kebabCase';
import { PortalTarget } from 'portal-vue';
import MutationTypes from '@/store/constants/mutations';
import GetterTypes from '@/store/constants/getters';

const isString = (value) => typeof value === 'string' || value instanceof String;
const isObject = (value) => value && typeof value === 'object' && value.constructor === Object;

const setRootElementCssVariables = (propName, value) => {
  if (value) {
    Object.keys(value).forEach((key) => {
      if (isString(value[key])) {
        document.documentElement.style.setProperty(`${propName}${kebabCase(key)}`, value[key]);
      } else if (isObject(value[key])) {
        setRootElementCssVariables(`${propName}${kebabCase(key)}-`, value[key]);
      }
    });
  }
};

export default {
  name: 'App',
  components: { PortalTarget },
  computed: {
    hasThemeBeenSet() {
      return this.$store.getters[GetterTypes.THEME] !== null;
    },
    isDarkModePreferred() {
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    },
  },
  watch: {
    selectedTheme: {
      handler() {
        this.updateTheme();
      },
      deep: true,
    },
  },
  mounted() {
    this.setTheme();
    this.updateTheme();
  },
  methods: {
    setTheme() {
      if (!this.hasThemeBeenSet) {
        this.setThemeInVuex(this.determineTheme());
      }
    },
    determineTheme() {
      if (process.env.VUE_APP_DEFAULT_THEME) {
        return process.env.VUE_APP_DEFAULT_THEME;
      }

      return this.isDarkModePreferred
        ? this.availableThemes.dark.name
        : this.availableThemes.sherbet.name;
    },
    setThemeInVuex(theme) {
      this.$store.commit(MutationTypes.SET_THEME, theme);
    },
    updateTheme() {
      setRootElementCssVariables('--', this.selectedTheme);
    },
  },
};
</script>

<style lang="scss">
  @import 'assets/styles/style.scss';

  .vue-portal-target {
    position: relative;
    height: 0;
    width: 0;
  }

  html {
    height: 100%;
    overflow: hidden;
  }

  body {
    height: 100%;
    font-family: 'roboto', sans-serif;
    font-size: 1em;
    color: var(--primary-text-hex);
  }

  @font-face {
    font-family: 'roboto';
    src: url('assets/fonts/Roboto-Regular.ttf') format('truetype');
    font-weight: 400;
    font-style: normal;
  }

  @font-face {
    font-family: 'roboto';
    src: url('assets/fonts/Roboto-Italic.ttf') format('truetype');
    font-weight: 400;
    font-style: italic;
  }

  @font-face {
    font-family: 'roboto';
    src: url('assets/fonts/Roboto-Medium.ttf') format('truetype');
    font-weight: 500;
    font-style: normal;
  }

  @font-face {
    font-family: 'roboto';
    src: url('assets/fonts/Roboto-Bold.ttf') format('truetype');
    font-weight: 700;
    font-style: normal;
  }

  @font-face {
    font-family: 'roboto-mono';
    src: url('assets/fonts/RobotoMono-Bold.ttf') format('truetype');
    font-weight: 700;
    font-style: normal;
  }

  @font-face {
    font-family: 'roboto-mono';
    src: url('assets/fonts/RobotoMono-Medium.ttf') format('truetype');
    font-weight: 500;
    font-style: normal;
  }

  @font-face {
    font-family: 'roboto-mono';
    src: url('assets/fonts/RobotoMono-Regular.ttf') format('truetype');
    font-weight: 400;
    font-style: normal;
  }

  a,
  a:visited,
  a:active {
    color: var(--primary-text-hex);
    text-decoration: none;
  }

  .login-transition-enter-active,
  .login-transition-leave-active {
    transition: opacity 0.4s;
  }

  .login-transition-enter,
  .login-transition-leave-to {
    opacity: 0;
  }

</style>
